import * as XLSX from 'xlsx';

export const ExcelExporter = {
  // Función auxiliar para obtener el nombre del mes en español con año
  getMonthName: (dateString) => {
    if (!dateString || dateString === 'Sin Fecha') return 'Sin Fecha';
    
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    
    try {
      const date = new Date(dateString);
      const monthName = months[date.getMonth()];
      const year = date.getFullYear();
      return `${monthName} ${year}`;
    } catch (error) {
      return 'Sin Fecha';
    }
  },

  // Función auxiliar para obtener la fecha completa para ordenamiento (más reciente primero)
  getDateForSorting: (dateString) => {
    if (!dateString || dateString === 'Sin Fecha') {
      return new Date('1900-01-01'); // Fecha muy antigua para poner al final
    }
    
    try {
      return new Date(dateString);
    } catch (error) {
      return new Date('1900-01-01');
    }
  },

  // Función auxiliar para obtener el número del mes para colores
  getMonthNumber: (dateString) => {
    if (!dateString) return 13; // Para fechas sin datos
    
    try {
      const date = new Date(dateString);
      return date.getMonth() + 1;
    } catch (error) {
      return 13;
    }
  },



  exportToExcel: (data, filename = 'data_export', sheetName = 'Sheet1', organizeByMonth = false) => {
    try {
      // Crear un nuevo workbook
      const workbook = XLSX.utils.book_new();
      
      let processedData = data;
      
      if (organizeByMonth) {
        // Agregar columna de mes con año y ordenar de más reciente a menos reciente
        processedData = data.map(item => ({
          Mes: ExcelExporter.getMonthName(item['Última Clase Dada'] || item.lastHeldClassDate),
          ...item
        })).sort((a, b) => {
          // Ordenar de más reciente a menos reciente
          const dateA = ExcelExporter.getDateForSorting(a['Última Clase Dada'] || a.lastHeldClassDate);
          const dateB = ExcelExporter.getDateForSorting(b['Última Clase Dada'] || b.lastHeldClassDate);
          
          // Comparar fechas: más reciente primero (orden descendente)
          if (dateB.getTime() !== dateA.getTime()) {
            return dateB.getTime() - dateA.getTime();
          }
          
          // Si las fechas son iguales, ordenar por nombre para mantener consistencia
          const nameA = (a['Nombre Completo'] || a['Nombre del Grupo'] || '').toLowerCase();
          const nameB = (b['Nombre Completo'] || b['Nombre del Grupo'] || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      }
      
      // Convertir los datos a worksheet
      const worksheet = XLSX.utils.json_to_sheet(processedData);
      

      
      // Agregar el worksheet al workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      
      // Generar el archivo Excel y descargarlo
      const fileName = `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      return true;
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      return false;
    }
  },



  // Función específica para profesores
  exportProfesores: (data, isActive = true) => {
    const processedData = data.map(profesor => ({
      id: profesor.id,
      id_user: profesor.id_user,
      'Nombre Completo': profesor.fullName,
      'Usuario': profesor.username,
      'Número de Teléfono': profesor.phoneNumber,
      'Contacto de Emergencia': profesor.emergencyContact,
      'Tipo de Identificación': profesor.identificationType,
      'Número de Identificación': profesor.identificationNumber,
      'Estado': profesor.status ? 'Activo' : 'Inactivo',
      'Última Clase Dada': profesor.lastHeldClassDate || 'Sin Fecha',
      'Fecha de Registro': profesor.registerDate,
    }));
    const filename = isActive ? 'profesores_activos_por_mes' : 'profesores_inactivos_por_mes';
    return ExcelExporter.exportToExcel(processedData, filename, 'Profesores', true);
  },

  // Función específica para estudiantes
  exportEstudiantes: (data) => {
    const processedData = data.map(estudiante => ({
      ID: estudiante.ID,
      'Nombre Completo': estudiante.fullName,
      'Email': estudiante.email,
      'Número de Teléfono': estudiante.phoneNumber,
      'Horas Compradas': estudiante.latestPurchasedHour?.hours || 'Sin datos',
      'Horas Restantes': estudiante.hoursRemaining,
      'Ciudad': estudiante.office,
      'ID del Profesor': estudiante.teacherDescription?.id || 'Sin asignar',
      'Profesor Asignado': estudiante.teacherDescription?.fullName || 'Sin asignar',
      'Estado': estudiante.status ? 'Activo' : 'Inactivo',
      'ID Última Compra': estudiante.latestPurchasedHour ? estudiante.latestPurchasedHour.id : 'Sin datos',
      'Estudiante ID (Compra)': estudiante.latestPurchasedHour ? estudiante.latestPurchasedHour.studentID : 'Sin datos',
      'Admin ID (Compra)': estudiante.latestPurchasedHour ? estudiante.latestPurchasedHour.adminID : 'Sin datos',
      'Horas Compradas (Última)': estudiante.latestPurchasedHour ? estudiante.latestPurchasedHour.hours : 'Sin datos',
      'Fecha Última Compra': estudiante.latestPurchasedHour ? estudiante.latestPurchasedHour.date : 'Sin datos',
      'Última Clase Dada': estudiante.lastHeldClassDate || 'Sin Fecha',
    }));

    return ExcelExporter.exportToExcel(processedData, 'estudiantes_privados_por_mes', 'Estudiantes', true);
  },

  // Función específica para grupos
  exportGrupos: (data) => {
    const processedData = data.map(grupo => ({
      ID: grupo.ID,
      'Nombre del Grupo': grupo.name,
      'ID de la Empresa': grupo.companyID,
      'Empresa': grupo.companyName,
      'NIT': grupo.companyNIT,
      'ID del Profesor': grupo.teacherDescription?.id || 'Sin asignar',
      'Profesor Asignado': grupo.teacherDescription?.fullName || 'Sin asignar',
      'Estado': grupo.status ? 'Activo' : 'Inactivo',
      'Última Clase Dada': grupo.lastHeldClassDate || 'Sin Fecha',
    }));

    return ExcelExporter.exportToExcel(processedData, 'grupos_empresas_por_mes', 'Grupos', true);
  }
};